import apiEndPoints from 'common/apiEndPoints';
import { performRequestFourth } from 'common/apiHandler';
import { getTenantDetails } from 'utils/utils';

/**
 * Lists email threads using the V2 RDS-backed endpoint with offset-based pagination.
 *
 * @param {object} params - Query parameters
 * @param {string} [params.caseId] - Filter by specific case ID
 * @param {boolean} [params.isSent=false] - true for sent emails, false for inbox
 * @param {boolean} [params.isUnread] - true to return only unread threads
 * @param {string} [params.mailDateLower] - ISO timestamp start date filter
 * @param {string} [params.mailDateUpper] - ISO timestamp end date filter
 * @param {string} [params.searchKeyword] - Search keyword for subject/content
 * @param {string} [params.mappingStatus] - Comma-separated mapping statuses
 * @param {string} [params.filterByUser] - Comma-separated email addresses
 * @param {number} [params.limit=20] - Records per page (max 100)
 * @param {number} [params.offset=0] - Records to skip
 * @param {boolean} [params.isEmailLog=false] - true for email log view
 * @param {'my_email'|'entire_firm'} [params.viewType] - Scope for thread list (sent as view_type)
 */
export const listEmailThreadsV2 = ({
  caseId,
  isSent = false,
  isUnread,
  mailDateLower,
  mailDateUpper,
  searchKeyword,
  mappingStatus,
  filterByUser,
  limit = 20,
  offset = 0,
  isEmailLog = false,
  viewType,
} = {}) => {
  const [tenantId, tenantSk] = getTenantDetails();

  const params = new URLSearchParams({
    tenant_id: tenantId,
    tenant_sk: tenantSk,
    is_sent: String(isSent),
    limit: String(limit),
    offset: String(offset),
  });

  if (caseId) params.append('case_id', caseId);
  if (isUnread) params.append('unread', 'true');
  if (mailDateLower) params.append('mail_date_lower', mailDateLower);
  if (mailDateUpper) params.append('mail_date_upper', mailDateUpper);
  if (searchKeyword) params.append('search_keyword', searchKeyword);
  if (mappingStatus) params.append('mapping_status', mappingStatus);
  if (filterByUser) params.append('filter_by_user', filterByUser);
  if (isEmailLog) params.append('is_email_log', 'true');
  if (viewType) params.append('view_type', viewType);

  return performRequestFourth(
    apiEndPoints.methodType.GET,
    `${apiEndPoints.listEmailThreadsV2}?${params.toString()}`,
    undefined,
    undefined,
    true,
    true
  );
};

/**
 * Retrieves all emails in a thread (parent + replies) using V2 RDS endpoint.
 * Also marks the thread as read for the current user.
 *
 * @param {string} emailThreadPk - Email thread partition key (URL encoded)
 * @param {string} emailThreadSk - Email thread sort key (URL encoded)
 * @param {number} [limit=10] - Records per page
 * @param {number} [offset=0] - Records to skip
 */
export const viewEmailThreadDetailsV2 = (emailThreadPk, emailThreadSk, limit = 10, offset = 0) => {
  const [tenantId, tenantSk] = getTenantDetails();

  const params = new URLSearchParams({
    tenant_id: tenantId,
    tenant_sk: tenantSk,
    email_thread_pk: emailThreadPk,
    email_thread_sk: emailThreadSk,
    limit: String(limit),
    offset: String(offset),
  });

  return performRequestFourth(
    apiEndPoints.methodType.GET,
    `${apiEndPoints.viewEmailThreadDetailsV2}?${params.toString()}`,
    undefined,
    undefined,
    true,
    true
  );
};

/**
 * Mark email threads read or unread (V2 RDS endpoint).
 *
 * @param {object} data
 * @param {'read'|'unread'} data.action
 * @param {string[]} data.email_thread_ids
 * @param {'my_email'|'entire_firm'} [data.view_type] - Same scope as list threads
 */
export const markEmailsReadUnreadV2 = (data) => {
  const [tenantId, tenantSk] = getTenantDetails();
  const query = new URLSearchParams({ tenant_id: tenantId, tenant_sk: tenantSk });
  return performRequestFourth(apiEndPoints.methodType.POST, `${apiEndPoints.markEmailsAsReadV2}?${query.toString()}`, data);
};
